import React, { useEffect, useState, useRef } from "react";

import { useQuery } from "react-query";
import { getBillingUsageByRange } from "./actions";

import moment from "moment";

import { Grid, Container, ThemeProvider } from "@mui/material";
import theme from "../../../material-ui-common/theme";

import {
  UsersDoughnut,
  UsageContainer,
  PlanBandwidthConsumption,
  TotalEventsScheduled,
  TotalSources,
  TranscriptionMinutes,
  LiveMinutes,
  LiveHoursStreamed,
  VodHoursPublished,
  StorageUsage,
  LiveAndVODBandwidthConsumption,
} from "./UsageItems";

import EventBroadcasts from "./eventBroadcasts";
import MostWatched from "./mostWatched";
import TopEventsByBandwidth from "./topEventsByBandwidth";
import DatePicker from "./datePicker";
import { humanFileSize, dataSizeStringToBytes } from "./utils";

const pieChartColors = [
  "rgba(0, 162, 255, 0.8)",
  "rgba(255, 159, 64, 0.8)",
  "rgba(255, 206, 86, 1)",
  "rgba(75, 192, 192, 1)",
  "rgba(255, 100, 100, 1)",
  "rgba(153, 102, 190, 1)",
];

const formatString = "YYYY-MM-DD";
const minDate = "2021-05-01";

const UsageView = ({ clientID }) => {
  const [dateFilter, setDateFilter] = useState({
    startDate: moment().startOf("month").format(formatString),
    endDate: moment().endOf("month").format(formatString),
  });

  const [billingInfo, setBillingInfo] = useState({});

  const { refetch: refetchBillingInfo, isLoading: isBillingLoading } = useQuery(
    {
      queryKey: `billingInfo`,
      queryFn: () =>
        getBillingUsageByRange({
          clientID,
          startDate: dateFilter.startDate,
          endDate: dateFilter.endDate,
        }),
      onSuccess: (data) => setBillingInfo(data),
    }
  );

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    refetchBillingInfo();
  }, [dateFilter]);

  let totalBytesAdjustedHuman = "0.00 GB";
  let bandwidthOverageBytesAdjustedHuman = "0.00 GB";
  let newBandwidthOveragePercentage = 0;
  let vodBytesAdjusted = 0;

  try {
    const liveBytes = billingInfo.bytes_Live_or_247;
    const p2pBytes = billingInfo.bytes_p2p;
    const vodBytes = billingInfo.bytes_VOD;

    vodBytesAdjusted = vodBytes / 0.88;

    const totalBytes = liveBytes + p2pBytes + vodBytes;
    const totalBytesAdjusted = liveBytes + p2pBytes + vodBytesAdjusted;
    totalBytesAdjustedHuman = humanFileSize(totalBytesAdjusted, true);

    const [amount, unit] = billingInfo.bandwidth_overage ? billingInfo.bandwidth_overage.replace(",", "").split(" ") : [0, "GB"];

    const bandwidthOverageBytes = dataSizeStringToBytes[unit](amount);

    // Calculate the adjustment for bandwidth overage
    const vodDelta = vodBytesAdjusted * 0.12;
    const bandwidthOverageBytesAdjusted = bandwidthOverageBytes === 0 ? 0 : bandwidthOverageBytes + vodDelta;
    bandwidthOverageBytesAdjustedHuman = humanFileSize(bandwidthOverageBytesAdjusted, true);

    // New bandwidth overage percentage
    const originalTotalBandwidthBytes = totalBytes / (billingInfo.bandwidth_perc_of_plan / 100);
    newBandwidthOveragePercentage = ((totalBytesAdjusted / originalTotalBandwidthBytes) * 100).toFixed(2);
  } catch (error) {
    console.log(error);
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth={false}>
        <DatePicker
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          formatString={formatString}
          minimumDate={minDate}
        />

        <Grid container spacing={3}>
          {/* Section: Storage Usage */}
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <UsageContainer xs={12}>
              <PlanBandwidthConsumption
                bandwidthPercentage={newBandwidthOveragePercentage || 0}
                bandwidthTotal={totalBytesAdjustedHuman || "0.00 GB"}
                bandwidthOverage={bandwidthOverageBytesAdjustedHuman || "0.00 GB"}
              />
            </UsageContainer>

            <UsageContainer xs={12} gridStyles={{ mt: 3 }}>
              <StorageUsage
                storageTotal={billingInfo.storage_used || "0.00 GB"}
                storagePercentage={billingInfo.storage_perc_of_plan || 0}
                storageOverage={billingInfo.storage_overage || "0.00 GB"}
                unlimitedStorage={billingInfo.storage_unlimited}
              />
            </UsageContainer>
          </Grid>

          <UsageContainer xs={12} sm={12} md={6} lg={6}>
            <LiveAndVODBandwidthConsumption
              data={[
                { type: "LIVE", val: billingInfo.bytes_Live_or_247 + billingInfo.bytes_p2p || 0 },
                { type: "VOD", val: vodBytesAdjusted || 0 },
              ]}
            />
          </UsageContainer>

          {/* Section: Top Consumers */}
          <UsageContainer xs={12} md={12} lg={12}>
            <TopEventsByBandwidth
              clientID={clientID}
              startDate={dateFilter.startDate}
              endDate={dateFilter.endDate}
            />
          </UsageContainer>

          {/* Section: Live Viewing Hours, Events Scheduled, Vod Viewing Hours  */}
          <Grid item xs={12} md={6} lg={6}>
            <LiveHoursStreamed
              clientID={clientID}
              startDate={dateFilter.startDate}
              endDate={dateFilter.endDate}
            />
            <TotalEventsScheduled clientID={clientID} />
            <VodHoursPublished
              clientID={clientID}
              startDate={dateFilter.startDate}
              endDate={dateFilter.endDate}
            />
          </Grid>

          {/* Section: Live Minutes, Total Sources, Transcription Minutes */}
          <Grid item xs={12} md={6} lg={6}>
            <LiveMinutes
              minutes={billingInfo.live_captioning_minutes || 0}
              isLoading={isBillingLoading}
            />
            <TotalSources clientID={clientID} />
            <TranscriptionMinutes
              minutes={billingInfo.transcription_minutes || 0}
              isLoading={isBillingLoading}
            />
          </Grid>

          {/* Section: Events Broadcasted */}
          <UsageContainer xs={12} md={12} lg={12}>
            <EventBroadcasts clientID={clientID} />
          </UsageContainer>

          {/* Section: Users Chart */}
          {/* <UsageContainer xs={12} md={12} lg={12}>
            <UsersDoughnut
              backgroundColor={pieChartColors}
              clientID={clientID}
            />
          </UsageContainer>  */}

          {/* Section: Most Watched */}
          <UsageContainer xs={12} md={12} lg={12}>
            <MostWatched
              clientID={clientID}
              startDate={dateFilter.startDate}
              endDate={dateFilter.endDate}
            />
          </UsageContainer>
        </Grid>
      </Container>
    </ThemeProvider>
  );
};
export default UsageView;
